import React, { useEffect, useMemo, useState, useRef } from 'react';
import AnalyticsInterval from '../../../models/analytics/AnalyticsInterval';
import AnalyticsRequestFilter from '../../../models/analytics/AnalyticsRequestFilter';
import SessionAnalyticsContainer, { SessionListModel } from '../../../hooks/SessionAnalyticsContainer';
import styled from '@emotion/styled';
import { Dictionary } from 'lodash';
import SsmlContainer from '../../../state/containers/SsmlContainer';
import UserApplicationPreferencesContainer from '../../../state/containers/UserApplicationPreferencesContainer';
import ContentHitEvent from '../../../models/analytics/api/ContentHitEvent';
import CustomAssistantResponse from '../../../models/customAssistant/CustomAssistantResponse';
import CustomScrollbars from '../../../components/structure/CustomScrollbars';
import useWindowSize from '../../../hooks/UseWindowSize';
import Loader from '../../../components/general/Loader';
import SimulatorConversationHistoryItem from '../../simulator/components/SimulatorConversationHistoryItem';
import IGenericContentContainer from '../../../state/definitions/IGenericContentContainer';
import ContentItemModel from '../../../models/features/api/ContentItemModel';
import SimulatorContainer from '../../../hooks/SimulatorContainer';
import moment from 'moment';
import { color_colors_ocean, color_shades_dark, color_shades_darkest, color_shades_lighter, color_text_light } from '../../../constants/colors';
import { SESSION_DATE_FORMAT } from '../../../models/analytics/AnalyticsDateFormat';
import ConversationTurn from '../../../models/sessionAttributes/ConversationTurn';
import qs from 'query-string';
import SearchField from '../../../components/forms/SearchField';
import ApplicationEnvironmentsContainer from '../../../state/containers/ApplicationEnvironmentsContainer';
import * as voicifyApi from '../../../api';
import CustomAssistantRequest from '../../../models/customAssistant/CustomAssistantRequest';
import SessionReview from './SessionReview';
import CodeOutputModal from '../../simulator/components/CodeOutputModal';
import SparkContainer from '../../../hooks/SparkContainer';
import useIsMobile from '../../../hooks/UseIsMobile';
import CollapsingPanel from '../../../components/structure/Panels/CollapsingPanel';
import CollapsedMenuPanel from '../../../components/structure/CollapsedMenuPanel';
import ExpandedMenuPanel from '../../../components/structure/ExpandedMenuPanel';
import { breakpoint_small } from '../../../constants/breakpoints';
import ContentContainer from '../../../hooks/ContentContainer';
import ApplicationModel from '../../../models/applications/api/ApplicationModel';
import { parseAnalyticsDate } from '../../../utils/engagementAnalyticsTransformers';

interface ApplicationSessionAnalyticsProps {
    interval: AnalyticsInterval
    innerInterval: AnalyticsInterval
    filter: AnalyticsRequestFilter
    applicationId: string,
    ssmlContainer: SsmlContainer
    userApplicationPreferencesContainer: UserApplicationPreferencesContainer
    contentContainers: Dictionary<IGenericContentContainer<ContentItemModel, any, any, any>>
    history: any
    envContainer: ApplicationEnvironmentsContainer,
    from?: string
    application: ApplicationModel
}

const ApplicationSessionAnalytics = (props: ApplicationSessionAnalyticsProps) => {
    const DEFAULT_VISIBLE_COUNT = 100;
    const SEARCH_MAX_VISIBLE_COUNT = 200;
    const SESSION_WINDOW_BEFORE = 50;
    const SESSION_WINDOW_AFTER = 50;
    const LOAD_MORE_COUNT = 50;

    const [selectedSession, setSelectedSession] = useState("");
    const [sessionToLoad, setSessionToLoad] = useState([]);
    const [allEvents, setAllEvents] = useState([]);
    const [contentHits, setContentHits] = useState([]);
    const [filteredSessions, setFilteredSessions] = useState([] as SessionListModel[]);
    const [loaded, setLoaded] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [eventsToShow, setEventsToShow] = useState(null as any);
    const [endDate, setEndDate] = useState("");
    const [detailedAnalyticsEnabled, setDetailedAnalyticsEnabled] = useState(true);
    const [searchFieldValue, setSearchFieldValue] = useState('');
    const [isLeftExpanded, setIsLeftExpanded] = useState<boolean>(true);
    const [touchStartY, setTouchStartY] = useState<number>(0);
    const [touchStartTime, setTouchStartTime] = useState<number>(0);
    const [mobileViewMode, setMobileViewMode] = useState<'session' | 'history'>('session');
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: DEFAULT_VISIBLE_COUNT - 1 });

    const simulatorContainer = SimulatorContainer.useContainer();
    const sessionContainer = SessionAnalyticsContainer.useContainer();
    const sparkContainer = SparkContainer.useContainer();
    const contentContainer = ContentContainer.useContainer();
    const windowSize = useWindowSize();
    const isMobile = useIsMobile();

    const sessionPillRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const sessionListScrollRef = useRef<any>(null);
    const lastSearchActiveRef = useRef(false);
    // Track if the session change was triggered by user selection
    const userSelectedSessionRef = useRef(false);

    // Reset userSelectedSessionRef when navigating to this page (e.g., via React Router Link)
    useEffect(() => {
        userSelectedSessionRef.current = false;
    }, [window.location.pathname]);

    useEffect(() => {
        const query = qs.parse(window.location.search);
        const search = query?.initialSearch as string;
        if (search?.length) {
            setSearchFieldValue(search);
        }
        const sessionId = query?.sessionId as string;
        if (isMobile && sessionId?.length) {
            setIsLeftExpanded(false);
        }
    }, [window.location.search, isMobile]);

    useEffect(() => {
        if (isMobile && selectedSession?.length) {
            setIsLeftExpanded(false);
        }
    }, [isMobile, selectedSession]);

    useEffect(() => {
        const query = qs.parse(window.location.search);
        const selectedId = query?.sessionId as string;
        // Only scroll if the session change was NOT triggered by user selection
        if (!userSelectedSessionRef.current && selectedId) {
            // Wait for the DOM to paint the session pill
            const timeout = setTimeout(() => {
                const pill = sessionPillRefs.current[selectedId];
                if (pill) {
                    pill.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
                }
                userSelectedSessionRef.current = false;
            }, 0);
            return () => clearTimeout(timeout);
        } else {
            userSelectedSessionRef.current = false;
        }
    }, [window.location.search, filteredSessions]);

    useEffect(() => {
        if (!filteredSessions.length) {
            setVisibleRange({ start: 0, end: -1 });
            return;
        }
        const searchActive = searchFieldValue?.length > 0;
        const selectedIndex = filteredSessions.findIndex(session => session.sessionId === selectedSession);
        const maxVisibleCount = searchActive ? SEARCH_MAX_VISIBLE_COUNT : Number.MAX_SAFE_INTEGER;
        const baseRange = (() => {
            if (selectedIndex >= 0) {
                const start = Math.max(0, selectedIndex - SESSION_WINDOW_BEFORE);
                const end = Math.min(filteredSessions.length - 1, selectedIndex + SESSION_WINDOW_AFTER);
                return { start, end };
            }
            return { start: 0, end: Math.min(filteredSessions.length - 1, DEFAULT_VISIBLE_COUNT - 1) };
        })();
        const shouldReset = searchActive !== lastSearchActiveRef.current;
        lastSearchActiveRef.current = searchActive;
        setVisibleRange((prev) => {
            if (shouldReset || prev.end < 0) {
                return baseRange;
            }
            if (selectedIndex >= 0 && (selectedIndex < prev.start || selectedIndex > prev.end)) {
                return baseRange;
            }
            const normalizedStart = Math.max(0, Math.min(prev.start, filteredSessions.length - 1));
            const normalizedEnd = Math.max(normalizedStart, Math.min(prev.end, filteredSessions.length - 1));
            const visibleCount = normalizedEnd - normalizedStart + 1;
            if (visibleCount > maxVisibleCount) {
                const adjustedEnd = Math.min(filteredSessions.length - 1, normalizedStart + maxVisibleCount - 1);
                return { start: normalizedStart, end: adjustedEnd };
            }
            return { start: normalizedStart, end: normalizedEnd };
        });
        if (shouldReset && sessionListScrollRef.current?.scrollTop) {
            sessionListScrollRef.current.scrollTop(0);
        }
    }, [filteredSessions, selectedSession, searchFieldValue]);

    const visibleSessions = useMemo(() => {
        if (!filteredSessions.length || visibleRange.end < visibleRange.start) {
            return [] as SessionListModel[];
        }
        return filteredSessions.slice(visibleRange.start, visibleRange.end + 1);
    }, [filteredSessions, visibleRange]);

    const handleSessionListScroll = (values) => {
        if (!filteredSessions.length) return;
        const searchActive = searchFieldValue?.length > 0;
        const maxVisibleCount = searchActive ? SEARCH_MAX_VISIBLE_COUNT : Number.MAX_SAFE_INTEGER;
        const totalCount = filteredSessions.length;
        const visibleCount = visibleRange.end >= visibleRange.start ? (visibleRange.end - visibleRange.start + 1) : 0;
        if (values.top > 0.85) {
            if (visibleCount >= maxVisibleCount) return;
            setVisibleRange((prev) => {
                const newEnd = Math.min(totalCount - 1, prev.end + LOAD_MORE_COUNT, prev.start + maxVisibleCount - 1);
                if (newEnd === prev.end) return prev;
                return { ...prev, end: newEnd };
            });
        }
        if (values.top < 0.15 && selectedSession?.length) {
            if (visibleCount >= maxVisibleCount) return;
            setVisibleRange((prev) => {
                const newStart = Math.max(0, prev.start - LOAD_MORE_COUNT);
                if (newStart === prev.start) return prev;
                return { ...prev, start: newStart };
            });
        }
    };

    useEffect(() => {
        simulatorContainer.startNewSession();
        initialLoad();
    }, [])

    useEffect(() => {
        if (selectedSession?.length
            && selectedSession in sessionContainer.events
            && (!allEvents.length || allEvents[0].sessionId !== selectedSession)) {
            const newEvents = sessionContainer.events[selectedSession];
            // sort by event date
            newEvents.sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime());
            setAllEvents(newEvents);
        }
    }, [sessionContainer.events, selectedSession])

    useEffect(() => {
        const preloadSessionDetailsAndStart = async () => {
            const contentHits = allEvents?.filter(e => e.eventType === "ContentHit");
            // group by req id
            const grouped = contentHits.reduce((acc, hit) => {
                if (!acc[hit.requestId]) {
                    acc[hit.requestId] = [];
                }
                acc[hit.requestId].push(hit);
                return acc;
            }, {} as Dictionary<ContentHitEvent[]>);
            // take last hit of each group
            const lastHits = Object.values(grouped).map((hits: []) => hits[hits.length - 1]);
            if (lastHits.length) {
                // Extract unique content items to preload
                const contentItemsToLoad = lastHits
                    .map(hit => ({
                        id: hit?.response?.contentItem?.contentItemId,
                        featureTypeId: hit?.featureTypeId
                    }))
                    .filter(item => item.id && item.featureTypeId)
                    .filter((item, index, self) => self.findIndex(t => t.id === item.id) === index);

                if (contentItemsToLoad.length > 0) {
                    try {
                        const bulkResult = await voicifyApi.bulkLoadDetails({
                            applicationId: props.applicationId,
                            content: contentItemsToLoad
                        });
                        if (bulkResult.resultType === "Ok" && bulkResult.data?.length) {
                            simulatorContainer.primeContentItemCache(bulkResult.data);
                        }
                    } catch (e) {
                        console.error("Failed to preload content items for session:", e);
                    }
                }

                setSessionToLoad(lastHits);
                setContentHits(lastHits);
            }
        };

        if (allEvents?.length) {
            preloadSessionDetailsAndStart();
        }
    }, [allEvents])

    useEffect(() => {
        if (sessionToLoad.length) {
            loadSessionTurn();
        }
    }, [sessionToLoad])

    useEffect(() => {
        let newFilteredSessions = sessionContainer.sessions;
        const query = qs.parse(window.location.search);
        if (query?.contentId?.length) {
            contentContainer.getSession(props.applicationId, query.contentId as string, props.filter).then(sessionIds => {
                if (sessionIds) {
                    setFilteredSessions(sessionContainer.sessions.filter(s => sessionIds.includes(s.sessionId)));
                }
            });

            return;
        }

        if (searchFieldValue?.length) {
            newFilteredSessions = [];
            for (const session of sessionContainer.sessions) {
                if (session.sessionId.includes(searchFieldValue))
                    newFilteredSessions.push(session);
                else if (session?.phoneNumbers?.some(p => p.includes(searchFieldValue))) {
                    newFilteredSessions.push(session);
                }
            }
        }
        setFilteredSessions(newFilteredSessions);
    }, [sessionContainer.sessions, searchFieldValue])

    useEffect(() => {
        if (props.envContainer.state.currentAppId === props.applicationId
            && props.envContainer.state.environments?.length) {
            setDetailedAnalyticsEnabled(props.envContainer.state.environments?.[0]?.detailedAnalyticsEnabled ?? false);
        }
    }, [props.envContainer.state.environments])

    useEffect(() => {
        if (startDate === "" && props.filter.startDate) {
            setStartDate(props.filter?.startDate);
            setEndDate(props.filter?.endDate);
            return;
        }
        if (props.filter?.startDate !== startDate || props.filter?.endDate !== endDate) {
            setStartDate(props.filter?.startDate);
            setEndDate(props.filter?.endDate);
            simulatorContainer.startNewSession();
            sessionContainer.loadSessions(props.applicationId, props.filter);
        }
    }, [props.filter])

    useEffect(() => {
        const query = qs.parse(window.location.search);
        if (query?.sessionId?.length) {
            if (selectedSession === query.sessionId)
                return;
            if (sessionContainer.sessions.some(s => s.sessionId === query.sessionId as string)) {
                setSelectedSession(query.sessionId as string);
            }
        }
    }, [window.location.search, sessionContainer.sessions])

    useEffect(() => {
        if (selectedSession.length == 0) return;
        sessionContainer.loadEventsForSession(props.applicationId, selectedSession);
        setLoaded(true);
    }, [selectedSession]);

    const convertEventsForCodeOutputModal = (events: any[]) => {
        var returnObj = {};
        events?.forEach((e, idx) => {
            returnObj[`${idx}-${e.eventType}`] = e;
        })

        // get last event that is request received
        const lastContentHit = events?.slice()?.reverse()?.find(e => e.eventType === "ContentHit");
        if (lastContentHit?.originalRequest?.sessionAttributes) {
            returnObj[`${events.length}-LatestSessionAttributes`] = lastContentHit.originalRequest.sessionAttributes;
        }

        return returnObj;
    }

    const memoizedModal = useMemo(
        () => (
            <CodeOutputModal
                title="JSON"
                closeModal={() => setEventsToShow(null)}
                data={convertEventsForCodeOutputModal(eventsToShow)}
                showCodeOutputModal={eventsToShow}
            />
        ),
        [eventsToShow],
    );

    const loadSessionTurn = async () => {
        const hit = sessionToLoad[0];
        const newResponses = [];
        newResponses.push(mapContentHitToResponse(hit))
        // we're adding the hit to the simulator container so that we can use the simulator to render the conversation
        await simulatorContainer.updateConversationHistory({
            id: hit.sessionId,
            sessionAttributes: {
                ...(hit.originalRequest?.sessionAttributes ?? {}),
                ...(hit.response?.additionalSessionAttributes ?? {})
            }
        },
            newResponses,
            props.contentContainers,
            mapContentHitToRequest(hit),
            mapContentHitToConversationTurn(hit)
        )
        setSessionToLoad(sessionToLoad.slice(1));
    }

    const mapContentHitToResponse = (hit: ContentHitEvent): CustomAssistantResponse => {
        let resposneContent = hit.response?.data?.content;
        if (hit?.response?.followUp?.content?.length)
            resposneContent = `${resposneContent} ${hit?.response?.followUp?.content}`;
        if (hit)
            return {
                responseId: hit.response?.data?.responseId,
                ssml: resposneContent,
                outputSpeech: resposneContent,
                displayText: resposneContent,
                displayTitle: resposneContent,
                endSession: false,
                hints: hit.response?.followUp?.followupHints ?? [],
                sessionAttributes: hit.originalRequest?.sessionAttributes ?? {},
                nlp: hit.originalRequest?.nlp,
                responseIdentifier: hit?.response?.data?.name?.length ? hit.response.data.name : hit.response?.data?.responseId?.length ? hit.response?.data?.responseId : null
            }
    }

    const mapContentHitToRequest = (hit: ContentHitEvent): CustomAssistantRequest => {
        let responseContent = hit.response?.data?.content;
        if (hit?.response?.followUp?.content?.length)
            responseContent = `${responseContent} ${hit?.response?.followUp?.content}`;
        if (hit)
            return hit?.originalRequest?.nativeRequest;
        else
            return {
                requestId: hit.requestId,
                context: {
                    sessionId: hit.sessionId,
                    noTracking: false,
                    requestType: hit.originalRequest?.requestType,
                    originalInput: hit.originalRequest?.originalInput,
                    channel: hit.originalRequest?.channel ?? "",
                    requiresLanguageUnderstanding: hit.originalRequest?.nativeRequest?.requiresLanguageUnderstanding ?? true,
                    locale: hit.originalRequest?.locale,
                },
                device: null,
                user: null
            };
    }

    const initialLoad = async () => {
        const envsResult = await props.envContainer.loadEnvironments(props.applicationId);
        if (envsResult.resultType !== 'Ok') {
            setDetailedAnalyticsEnabled(false);
            setLoaded(true);
            return;
        }
        const enabled = envsResult.data[0]?.detailedAnalyticsEnabled ?? false;
        setDetailedAnalyticsEnabled(enabled);
        if (!enabled) {
            setLoaded(true);
            return;
        }
        const query = qs.parse(window.location.search);
        sessionContainer.loadSessions(props.applicationId, props.filter);
        setLoaded(true);
    }

    const mapContentHitToConversationTurn = (hit: ContentHitEvent): ConversationTurn => {
        return {
            ContentId: hit?.response?.contentItem?.contentItemId,
            FeatureTypeId: hit?.featureTypeId,
            ApplicationId: props.applicationId,
            FollowUpId: hit?.response?.followUp?.id,
            HelpMessageId: '',
            FallbackMessageId: '',
            ChildTurns: [],
            IsLimitedToChildren: hit?.response?.followUp?.isLimitedToChildren,
            RequestDate: new Date(hit?.eventDate),
        }
    };

    const updateFilterToBeginningAndEndOfDay = (filter) => {
        const parsedStartDate = parseAnalyticsDate(filter.startDate);
        const parsedEndDate = parseAnalyticsDate(filter.endDate);

        if (!parsedStartDate.isValid() || !parsedEndDate.isValid()) {
            return filter;
        }

        return {
            ...filter,
            startDate: parsedStartDate.clone().startOf('day').toISOString(),
            endDate: parsedEndDate.clone().endOf('day').toISOString()
        }
    };

    const handleToggleExpandLeft = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, expanded: boolean) => {
        setIsLeftExpanded(expanded);
        localStorage.setItem("contentMenu", expanded?.toString());
        e?.stopPropagation();
    };

    const formatPhoneNumber = (phoneNumber: string) => {
        // Remove any non-digit characters from the input number
        const cleanedNumber = phoneNumber?.replace(/\D/g, '');
        const formattedNumber = `${cleanedNumber?.substring(7)}`;
        return formattedNumber;
    };

    const renderViewerWrapper = () => {
        const renderSessionView = () => (
            sessionContainer.loadingEvents ? <Loader /> :
                allEvents?.length ?
                    <SessionReviewContainer>
                        <SessionReview
                            showFrom
                            manualHeight={(windowSize.windowSize.innerHeight) - (isMobile ? 300 : 250)}
                            events={allEvents}
                            applicationId={props.applicationId}
                            hideBorder
                            simple={sessionContainer.simpleView || sparkContainer.inSpark}
                            setEventsToShow={setEventsToShow}
                            resetCopyButtonText={() => null}
                            application={props.application}
                            mobileBubbles={isMobile}
                            onSessionSelect={(sessionId) => {
                                if (window.location.search?.includes(sessionId)) return;
                                const session = filteredSessions.find(s => s.sessionId === sessionId);
                                if (session) {
                                    handleSessionSelection(session, { stopPropagation: () => null } as any);
                                    return;
                                }
                                const newFilter = updateFilterToBeginningAndEndOfDay(props.filter);
                                props.history.push({
                                    search: `?sessionId=${sessionId}&startDate=${newFilter.startDate}&endDate=${newFilter.endDate}&interval=${props.interval}`
                                });
                            }}

                        />
                    </SessionReviewContainer>
                    :
                    <>
                        <EmptyConversationText>
                            <h4>Select a session</h4>
                            <p>to view the conversation</p>
                        </EmptyConversationText>
                        <Loader static />
                    </>
        );

        const renderHistoryView = () => (
            <HistoryViewContainer>
                <CustomScrollbars
                    autoHide
                    autoHeight
                    autoHeightMin={(windowSize.windowSize.innerHeight) - (isMobile ? 300 : 250)}
                    autoHeightMax={(windowSize.windowSize.innerHeight) - (isMobile ? 300 : 250)}
                >
                    {simulatorContainer.contentItemsFromHistory?.length ? <HistoryHeader>{"CONVERSATION HISTORY"}</HistoryHeader> : null}
                    <HistoryList>
                        {simulatorContainer.contentItemsFromHistory.map((c, idx) =>
                            <SimulatorConversationHistoryItem
                                key={idx}
                                turn={mapContentHitToConversationTurn(contentHits[idx])}
                                content={c}
                                simple={sessionContainer.simpleView}
                                index={idx}
                            />
                        )}
                    </HistoryList>
                </CustomScrollbars>
            </HistoryViewContainer>
        );

        return (
            <ViewerWrapper isMobile={isMobile}>
                {isMobile && allEvents?.length > 0 && (
                    <MobileViewToggle>
                        <ToggleButton
                            active={mobileViewMode === 'session'}
                            onClick={() => setMobileViewMode('session')}
                        >
                            Session Review
                        </ToggleButton>
                        <ToggleButton
                            active={mobileViewMode === 'history'}
                            onClick={() => setMobileViewMode('history')}
                        >
                            Conversation History
                        </ToggleButton>
                    </MobileViewToggle>
                )}
                {isMobile ? (
                    mobileViewMode === 'session' ? renderSessionView() : renderHistoryView()
                ) : (
                    renderSessionView()
                )}
            </ViewerWrapper>
        )
    };

    const renderSessionList = () => {
        return (
            <SessionListWrapper>
                <CustomScrollbars
                    autoHide
                    autoHeight
                    autoHeightMin={(windowSize.windowSize.innerHeight) - 200}
                    autoHeightMax={(windowSize.windowSize.innerHeight) - 200}
                    onScrollFrame={handleSessionListScroll}
                    ref={sessionListScrollRef}>
                    <SessionList>
                        <SessionsHeader>
                            {sparkContainer.inSpark ?
                                <LeftHeader>{`${filteredSessions.length} CALLS`}</LeftHeader>
                                :
                                <LeftHeader>{`${filteredSessions.length} SESSIONS`}</LeftHeader>}
                        </SessionsHeader>
                        <StyledSearchField
                            id="searchField"
                            name={"searchField"}
                            placeholder={"Session or Content Item ID"}
                            value={searchFieldValue}
                            disabled={!loaded}
                            onChange={(e) => setSearchFieldValue(e.target.value)}
                            boxType={"square"}
                        />
                        {
                            visibleSessions.map((session, idx) => {
                                return (
                                    <SessionPill
                                        ref={el => { sessionPillRefs.current[session.sessionId] = el; }}
                                        className={`${(window.location.search?.includes(session.sessionId)) ? "selected" : ''} ${(idx === Object.keys(visibleSessions).length - 1) ? "last" : ''}`}
                                        key={session.sessionId}
                                        onClick={isMobile ? undefined : (e) => handleSessionSelection(session, e)}
                                        onTouchStart={isMobile ? handleTouchStart : undefined}
                                        onTouchEnd={isMobile ? (e) => handleTouchEnd(session, e) : undefined}
                                    >
                                        {/* show local time of session, RDS update is now returning server time */}
                                        {moment(session.startDate).format(SESSION_DATE_FORMAT)}
                                        {session?.from?.length ? <SessionPillFrom>{formatPhoneNumber(session.from)}</SessionPillFrom> : null}
                                    </SessionPill>
                                );
                            })
                        }
                    </SessionList>
                </CustomScrollbars>
            </SessionListWrapper>
        )
    };

    const renderMenu = () => {
        return (
            renderSessionList()
        )
    };

    const renderCollapsingPanel = () => {
        return (
            <CollapsingPanel
                isCollapsed={!isLeftExpanded}
                collapsedView={
                    <CollapsedMenuPanel
                        onExpand={(e) => handleToggleExpandLeft(e, true)}
                        expandedViewRender={renderMenu}
                        minimizedDrawer={isMobile}
                    />
                }
                expandedView={
                    <ExpandedMenuPanel
                        onCollapse={(e) => handleToggleExpandLeft(e, false)}
                        expandedViewRender={renderMenu}
                        minimizedDrawer={isMobile}
                    />
                }
            />
        );
    };

    const renderMobileView = () => {
        return (
            <SectionWrapper>
                {renderCollapsingPanel()}
                {renderViewerWrapper()}
                {memoizedModal}
            </SectionWrapper>
        )
    };

    const renderDesktopView = () => {
        return (!isMobile &&
            <SectionWrapper>
                {renderSessionList()}
                {renderViewerWrapper()}
                <HistoryListWrapper>
                    <CustomScrollbars autoHide autoHeight autoHeightMin={(windowSize.windowSize.innerHeight) - 200} autoHeightMax={(windowSize.windowSize.innerHeight) - 200}>
                        {simulatorContainer.contentItemsFromHistory?.length ? <HistoryHeader>{"CONVERSATION HISTORY"}</HistoryHeader> : null}
                        <HistoryList>
                            {simulatorContainer.contentItemsFromHistory.map((c, idx) =>
                                <SimulatorConversationHistoryItem
                                    key={idx}
                                    turn={mapContentHitToConversationTurn(contentHits[idx])}
                                    content={c}
                                    simple={sessionContainer.simpleView}
                                    index={idx}
                                />
                            )}
                        </HistoryList>
                    </CustomScrollbars>
                </HistoryListWrapper>
                {memoizedModal}
            </SectionWrapper>
        )
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        setTouchStartY(e.touches[0].clientY);
        setTouchStartTime(Date.now());
    };

    const handleTouchEnd = (session: SessionListModel, e: React.TouchEvent<HTMLDivElement>) => {
        const touchEndY = e.changedTouches[0].clientY;
        const touchEndTime = Date.now();
        const deltaY = Math.abs(touchEndY - touchStartY);
        const deltaTime = touchEndTime - touchStartTime;

        // if the duratin of the touch was short, it was a tap(click), if not it was a scroll
        const isClick = deltaTime < 300 && deltaY < 10;

        if (isClick) {
            handleSessionSelection(session, e);
        }
    };

    const handleSessionSelection = (session: SessionListModel, e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        if (window.location.search?.includes(session.sessionId)) return;

        // Mark that this session change is user-initiated
        userSelectedSessionRef.current = true;

        const selectedIndex = filteredSessions.findIndex(s => s.sessionId === session.sessionId);
        if (selectedIndex >= 0) {
            const start = Math.max(0, selectedIndex - SESSION_WINDOW_BEFORE);
            const end = Math.min(filteredSessions.length - 1, selectedIndex + SESSION_WINDOW_AFTER);
            setVisibleRange({ start, end });
        }

        simulatorContainer.startNewSession();
        const newFilter = updateFilterToBeginningAndEndOfDay(props.filter);
        props.history.push({
            search: `?sessionId=${session.sessionId}&startDate=${newFilter.startDate}&endDate=${newFilter.endDate}&interval=${props.interval}`
        });

        if (isMobile) {
            handleToggleExpandLeft(e as any, false);
        }
    };

    return (
        <>
            {(!loaded || sessionContainer.loading || contentContainer.isLoading) ? (
                <LoaderWrapper><Loader /></LoaderWrapper>
            ) : !detailedAnalyticsEnabled ? (
                <>
                    <EmptyConversationText>
                        <h4>Enable detailed analytics</h4>
                        <p>to view sessions</p>
                    </EmptyConversationText>
                    <Loader static />
                </>
            ) : (
                isMobile ? renderMobileView() : renderDesktopView()
            )}
        </>
    );

};

const LoaderWrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    /* the spinner covers the whole screen; let clicks through so the rest of the app stays usable while sessions load */
    pointer-events: none;
    .loader-container {
        display: flex;
        align-items: center;
        justify-content: center;
        .spinner > div {
            background: ${color_colors_ocean};
        }
    }
`;

const SessionReviewContainer = styled.div`
    width: 100%;
    min-width: 0;
`;

const SessionList = styled.div`
    font-family: Muli;
    font-style: normal;
    font-weight: normal;
`;

const SectionWrapper = styled.div`
    display: flex;
    flex: 1;
`;

const HistoryListWrapper = styled.div`
    height: 100%;
    width: 60%;
    max-width: 432px;
    min-width: 300px;
`;

const HistoryList = styled.div`
    padding: 16px;
`;

const SessionListWrapper = styled.div`
    max-width: 432px;
    min-width: 300px;
`;

const SessionPill = styled.div`
    height: 56px;
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    background: white;
    margin: 0 24px 24px 24px;
    padding: 16px;
    position: relative;
    cursor: pointer;
    &.selected {
        background: ${color_shades_lighter};
    }
    &.last {
        margin-bottom: 32px;
    }
`;

const SessionPillFrom = styled.p`
    bottom: 4px;
    left: 16px;
    position: absolute;
    color: ${color_text_light};
    font-size: 12px;
`;

const StyledSearchField = styled(SearchField)`    
    margin: 0 24px 24px 24px;
`;

const ViewerWrapper = styled.div<{ isMobile?: boolean }>`
    height: calc(100vh - 200px);
    background: ${color_shades_lighter};
    width: 75%;
    flex: 1;
    min-width: 0;
    ${breakpoint_small} {
        width: 100%;
        height: calc(100vh - 175px);
    }
    ${props => props.isMobile ? `
        width: 100%;
        height: calc(100vh - 175px);
    ` : ''}
`;

const SessionsHeader = styled.div`
    margin: 24px 24px 16px 24px;
    display: flex;
    flex-direction: row;
`

const LeftHeader = styled.div`
    margin-right: auto;
`;

const HistoryHeader = styled.div`
    margin: 24px 0px 0px 24px;
`

const EmptyConversationText = styled.div`
    color: ${color_shades_darkest}; 
    position: absolute;
    top: 50%;
    left: 50%;
    margin: 60px 0 0 -95px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`

const HistoryViewContainer = styled.div`
    padding: 16px;
    height: 100%;
`;

const MobileViewToggle = styled.div`
    display: flex;
    background: white;
    border-bottom: 1px solid ${color_shades_dark};
    padding: 8px 16px;
`;

const ToggleButton = styled.button<{ active: boolean }>`
    flex: 1;
    padding: 8px 16px;
    margin: 0 4px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    background: ${({ active }) => (active ? color_shades_lighter : 'white')};
    color: ${({ active }) => (active ? color_shades_darkest : color_text_light)};
    font-size: 14px;
    font-weight: ${({ active }) => (active ? 'bold' : 'normal')};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${color_shades_lighter};
    }
`;
export default ApplicationSessionAnalytics;
